import { Fragment, useEffect, useMemo, useState, forwardRef } from 'react';
import Select, { components } from 'react-select';
import DatePicker from 'react-datepicker';
import CurrencyInput from 'react-currency-input-field';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';

import 'react-datepicker/dist/react-datepicker.css';

import { editarBreadcrumb } from '../../actions/breadcrumb';
import { setAlert } from '../../actions/alert';

import { buscarProveedores } from '../../actions/proveedores';
import {
    buscarFacturaDetalle,
    limpiarFacturaDetalle,
    cargarCategoriasFactura,
    cargarSubcategoriasFactura,
    cargarProductosFactura,
    crearProductoProveedorFactura,
    editarFactura
} from '../../actions/facturas';

import { CustomValueContainer } from '../../components/react_select/ReactSelect';

import NuevoProductoModal from './modals/NuevoProductoModal';
import ConfirmarGuardarFacturaModal from './modals/ConfirmarGuardarFacturaModal';

const toNum = (v) => {
    const n = Number(v);
    return Number.isNaN(n) ? 0 : n;
};

const round2 = (n) => {
    const x = Number(n);
    if (Number.isNaN(x)) return '';
    return (Math.round(x * 100) / 100).toString();
};

const moneyFmt = (v) => {
    const n = Number(v || 0);
    return new Intl.NumberFormat('es-MX', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(Number.isNaN(n) ? 0 : n);
};

const formatYYYYMMDD = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const toDateSafe = (v) => {
    if (!v) return null;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
};

const calcPrecioConPct = (costo, pct) => {
    const c = toNum(costo);
    const p = toNum(pct);
    if (!c || c <= 0) return '';
    return round2(c * (1 + (p / 100)));
};

const calcPrecioConPctNum = (costo, pct) => {
    const c = toNum(costo);
    const p = toNum(pct);
    if (!c || c <= 0) return 0;
    return Math.round((c * (1 + (p / 100))) * 100) / 100;
};

const pctDesdePrecio = (costo, precio) => {
    const c = toNum(costo);
    const pr = toNum(precio);
    if (!c || c <= 0) return '';
    return round2(((pr / c) - 1) * 100);
};

const trimOrNull = (v) => {
    const s = (v ?? '').toString().trim();
    return s ? s : null;
};

const DateInput = forwardRef(({ value, onClick, onChange, id }, ref) => {
    return (
        <input
            type="text"
            className="form-control text-center"
            style={{ height: '58px' }}
            onClick={onClick}
            onChange={onChange}
            value={value}
            ref={ref}
            id={id}
            placeholder="Fecha"
            autoComplete="off"
            readOnly
        />
    );
});

const styles_select = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    container: (provided) => ({ ...provided }),
    valueContainer: (provided) => ({
        ...provided,
        overflow: 'visible',
        marginTop: '10px'
    }),
    placeholder: (provided, state) => ({
        ...provided,
        position: 'absolute',
        top: state.hasValue || state.selectProps.inputValue ? -15 : '0%',
        transition: 'top 0.1s, font-size 0.1s',
        fontSize: (state.hasValue || state.selectProps.inputValue) && 13
    })
};

const EditarFactura = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const { listado: listado_proveedores } = useSelector((state) => state.proveedores);

    const {
        categorias,
        subcategorias,
        productos_catalogo,
        detalle
    } = useSelector((state) => state.facturas);

    const [fecha, setFecha] = useState(new Date());
    const [proveedorSel, setProveedorSel] = useState(null);
    const [numeroFactura, setNumeroFactura] = useState('');
    const [montoReal, setMontoReal] = useState('');

    const [moneda, setMoneda] = useState(0);
    const [tipoCambioUsd, setTipoCambioUsd] = useState('1');

    const [showConfirmarFactura, setShowConfirmarFactura] = useState(false);
    const [guardandoFactura, setGuardandoFactura] = useState(false);

    const getTc = () => {
        const tc = toNum(tipoCambioUsd);
        return tc > 0 ? tc : 1;
    };

    const validarTipoCambioSiUSD = () => {
        if (Number(moneda) !== 1) return true;
        return toNum(tipoCambioUsd) > 0;
    };

    const convertirNumero = (n, de, a, tc) => {
        const x = toNum(n);
        if (!x) return 0;
        if (de === a) return Math.round(x * 100) / 100;
        if (de === 0 && a === 1) return Math.round((x / tc) * 100) / 100;
        return Math.round((x * tc) * 100) / 100;
    };

    const convertirString = (s, de, a, tc) => {
        const x = toNum(s);
        if (!x) return '';
        if (de === a) return round2(x);
        if (de === 0 && a === 1) return round2(x / tc);
        return round2(x * tc);
    };

    const onChangeMoneda = (next) => {
        const nextVal = Number(next);
        const prevVal = Number(moneda);
        if (nextVal === prevVal) return;

        const tc = getTc();

        setMontoReal((prev) => convertirString(prev, prevVal, nextVal, tc));
        setPrecioUnitario((prev) => convertirString(prev, prevVal, nextVal, tc));
        setPrecioPublico((prev) => convertirString(prev, prevVal, nextVal, tc));
        setPrecioMinimo((prev) => convertirString(prev, prevVal, nextVal, tc));
        setPrecioLiquidacion((prev) => convertirString(prev, prevVal, nextVal, tc));

        setProductosFactura((prev) => {
            return (prev || []).map((p) => ({
                ...p,
                precio_unitario: convertirNumero(p?.precio_unitario, prevVal, nextVal, tc),
                precio_publico: convertirNumero(p?.precio_publico, prevVal, nextVal, tc),
                precio_minimo: convertirNumero(p?.precio_minimo, prevVal, nextVal, tc),
                precio_liquidacion: convertirNumero(p?.precio_liquidacion, prevVal, nextVal, tc)
            }));
        });

        setMoneda(nextVal);
    };

    const [categoriaSel, setCategoriaSel] = useState(null);
    const [subcategoriaSel, setSubcategoriaSel] = useState(null);
    const [productoSel, setProductoSel] = useState(null);

    const [codigoInra, setCodigoInra] = useState('');
    const [codigoProveedor, setCodigoProveedor] = useState('');
    const [cantidad, setCantidad] = useState('1');
    const [unidadSel, setUnidadSel] = useState('');

    const [precioUnitario, setPrecioUnitario] = useState('');

    const [pctPublico, setPctPublico] = useState('20');
    const [pctMinimo, setPctMinimo] = useState('10');
    const [pctLiquidacion, setPctLiquidacion] = useState('5');

    const [precioPublico, setPrecioPublico] = useState('');
    const [precioMinimo, setPrecioMinimo] = useState('');
    const [precioLiquidacion, setPrecioLiquidacion] = useState('');
    const [impuestos, setImpuestos] = useState('0');

    const [productosFactura, setProductosFactura] = useState([]);
    const [modalNuevoProducto, setModalNuevoProducto] = useState(false);

    useEffect(() => {
        dispatch(editarBreadcrumb([
            { activo: false, nombre: 'Facturas', path: '/facturas' },
            { activo: true, nombre: 'Editar factura', path: `/facturas/editar/${id}` }
        ]));
    }, [dispatch, id]);

    useEffect(() => {
        dispatch(buscarProveedores({}));
        dispatch(cargarCategoriasFactura());
        dispatch(buscarFacturaDetalle(id));

        return () => {
            dispatch(limpiarFacturaDetalle());
        };
    }, [dispatch, id]);

    const opcionesProveedores = useMemo(() => {
        return (listado_proveedores || []).map((p) => ({
            value: p._id,
            label: p.nombre || p.razon_social || p.codigo || 'Proveedor'
        }));
    }, [listado_proveedores]);

    const opcionesCategorias = useMemo(() => {
        return (categorias || []).map((c) => ({
            value: c._id,
            label: c.nombre || 'Categoría'
        }));
    }, [categorias]);

    const opcionesSubcategorias = useMemo(() => {
        return (subcategorias || []).map((s) => ({
            value: s._id,
            label: s.nombre || 'Subcategoría'
        }));
    }, [subcategorias]);

    const opcionesProductos = useMemo(() => {
        return (productos_catalogo || []).map((p) => {
            const nombre = p.nombre || 'Producto';
            const codTienda = p.codigo_tienda ?? p.codigo_inra ?? p.codigo ?? '';
            const codProv = p.codigo_proveedor ?? '';

            const label = [
                nombre,
                codTienda ? `(${codTienda})` : '',
                codProv ? `- Prov: ${codProv}` : ''
            ].filter(Boolean).join(' ');

            return {
                value: p._id,
                label,
                raw: p
            };
        });
    }, [productos_catalogo]);

    const filterProductoOption = (option, inputValue) => {
        const q = (inputValue || '').toLowerCase().trim();
        if (!q) return true;

        const raw = option?.data?.raw || {};
        const haystack = [
            option?.label,
            raw?.nombre,
            raw?.codigo_tienda,
            raw?.codigo_inra,
            raw?.codigo,
            raw?.codigo_proveedor
        ].filter(Boolean).join(' ').toLowerCase();

        return haystack.includes(q);
    };

    const opcionesUnidades = useMemo(() => {
        const raw = productoSel?.raw;
        const arr = Array.isArray(raw?.unidades) ? raw.unidades : [];
        const map = new Map();

        arr.forEach((x) => {
            const tipo = Number(x?.tipo);
            if (tipo !== 1) return;

            const idu = x?.unidad?._id || x?.id_unidad || '';
            if (!idu) return;

            const nombreBase =
                x?.unidad?.nombre ||
                x?.unidad_nombre ||
                x?.nombre ||
                'Unidad';

            const cant = x?.cantidad !== null && x?.cantidad !== undefined ? x.cantidad : '';
            const label = cant !== '' ? `${nombreBase} (${cant})` : nombreBase;

            if (!map.has(idu)) map.set(idu, { value: idu, label });
        });

        return Array.from(map.values());
    }, [productoSel]);

    useEffect(() => {
        if (!productoSel?.value) return;

        const lista = Array.isArray(productos_catalogo) ? productos_catalogo : [];
        const match = lista.find(p => String(p?._id) === String(productoSel.value));
        if (!match) return;

        setProductoSel(prev => {
            if (!prev?.value) return prev;
            if (String(prev.value) !== String(match._id)) return prev;
            if (prev.raw === match) return prev;
            return { ...prev, raw: match };
        });
    }, [productos_catalogo, productoSel?.value]);

    useEffect(() => {
        if (!productoSel?.raw) return;

        const arr = Array.isArray(productoSel.raw?.unidades) ? productoSel.raw.unidades : [];
        const opts = arr
            .filter(u => Number(u?.tipo) === 1)
            .map(u => ({
                value: u?.unidad?._id || u?.id_unidad || '',
                label: u?.unidad?.nombre || u?.unidad_nombre || u?.nombre || 'Unidad'
            }))
            .filter(x => x.value);

        if (opts.length === 1) setUnidadSel(opts[0].value);
    }, [productoSel]);

    useEffect(() => {
        if (!detalle?._id) return;

        if (Number(detalle.status) !== 0) {
            dispatch(setAlert('Solo se pueden editar facturas con status Pendiente', 'danger'));
            navigate('/facturas');
            return;
        }

        const f = toDateSafe(detalle?.fecha) || new Date();
        setFecha(f);

        const mon = Number(detalle?.moneda) === 1 ? 1 : 0;
        const tcDet = toNum(detalle?.tipo_cambio) > 0 ? toNum(detalle?.tipo_cambio) : 1;

        setMoneda(mon);
        setTipoCambioUsd(mon === 1 ? `${tcDet}` : '1');

        const montoMx = ((detalle?.monto_real ?? detalle?.monto) ?? '');
        const montoShow = mon === 1 ? round2(toNum(montoMx) / tcDet) : `${montoMx}`;
        setMontoReal(montoShow);

        setNumeroFactura(detalle?.numero || '');

        if (detalle?.proveedor?._id) {
            setProveedorSel({
                value: detalle.proveedor._id,
                label: detalle.proveedor.nombre || detalle.proveedor.razon_social || 'Proveedor'
            });
        }

        const prods = (detalle?.productos || []).map((p) => {
            const prodId = p?.producto?._id || p?._id;

            const unidadId =
                (typeof p?.unidad === 'string' && p?.unidad) ? p.unidad :
                (p?.unidad?._id ? p.unidad._id : null);

            const unidadNombre = p?.unidad?.nombre ? p.unidad.nombre : '';

            const unitMx = Number(p?.precio_unitario || 0);
            const pubMx = Number(p?.precio_publico || 0);
            const minMx = Number(p?.precio_minimo || 0);
            const liqMx = Number(p?.precio_liquidacion || 0);

            const unitShow = mon === 1 ? Math.round((unitMx / tcDet) * 100) / 100 : unitMx;
            const pubShow = mon === 1 ? Math.round((pubMx / tcDet) * 100) / 100 : pubMx;
            const minShow = mon === 1 ? Math.round((minMx / tcDet) * 100) / 100 : minMx;
            const liqShow = mon === 1 ? Math.round((liqMx / tcDet) * 100) / 100 : liqMx;

            return {
                _id: prodId,
                nombre: p?.nombre || p?.producto?.nombre || 'Producto',
                codigo_tienda: p?.codigo_tienda || '',
                codigo_proveedor: p?.codigo_proveedor || '',
                cantidad_surtir: Number(p?.cantidad_surtir || 0),
                precio_unitario: unitShow,
                precio_publico: pubShow,
                precio_minimo: minShow,
                precio_liquidacion: liqShow,
                pct_publico: unitShow > 0 ? pctDesdePrecio(unitShow, pubShow) : '',
                pct_minimo: unitShow > 0 ? pctDesdePrecio(unitShow, minShow) : '',
                pct_liquidacion: unitShow > 0 ? pctDesdePrecio(unitShow, liqShow) : '',
                impuestos: Number(p?.impuestos || 0),
                unidad: unidadId,
                unidad_nombre: unidadNombre
            };
        });

        setProductosFactura(prods);
    }, [detalle]);

    useEffect(() => {
        if (!proveedorSel?.value) return;

        dispatch(cargarProductosFactura({
            id_proveedor: proveedorSel.value,
            id_categoria: categoriaSel?.value || null,
            id_subcategoria: subcategoriaSel?.value || null,
            nombre: null,
            codigo: null,
            tipo: 1,
            codigo_proveedor: null
        }));
    }, [proveedorSel?.value, categoriaSel?.value, subcategoriaSel?.value, dispatch]);

    useEffect(() => {
        if (!proveedorSel?.value) return;
        if (productoSel?.value) return;

        const codigo = trimOrNull(codigoInra);
        const codProv = trimOrNull(codigoProveedor);

        const t = setTimeout(async () => {
            await dispatch(
                cargarProductosFactura({
                    id_proveedor: proveedorSel.value,
                    id_categoria: categoriaSel?.value || null,
                    id_subcategoria: subcategoriaSel?.value || null,
                    nombre: null,
                    codigo: codigo,
                    tipo: 1,
                    codigo_proveedor: codProv
                })
            );
        }, 350);

        return () => clearTimeout(t);
    }, [
        codigoInra,
        codigoProveedor,
        proveedorSel?.value,
        categoriaSel?.value,
        subcategoriaSel?.value,
        productoSel?.value,
        dispatch
    ]);

    const resetInputsProducto = () => {
        setProductoSel(null);
        setUnidadSel('');
        setCodigoInra('');
        setCodigoProveedor('');
        setCantidad('1');
        setPrecioUnitario('');
        setPctPublico('20');
        setPctMinimo('10');
        setPctLiquidacion('5');
        setPrecioPublico('');
        setPrecioMinimo('');
        setPrecioLiquidacion('');
        setImpuestos('0');
    };

    const resetSeccionProductosCompleta = () => {
        setCategoriaSel(null);
        setSubcategoriaSel(null);
        resetInputsProducto();
    };

    const syncCategoriaSubcategoriaDesdeProducto = async (raw = {}) => {
        if (!proveedorSel?.value) return;

        const catId = raw?.categoria?._id || null;
        const catNombre = raw?.categoria?.nombre || '';

        const subId = raw?.subcategoria?._id || null;
        const subNombre = raw?.subcategoria?.nombre || '';

        let nextCat = categoriaSel;
        let nextSub = subcategoriaSel;

        if (!categoriaSel?.value && catId) {
            nextCat = { value: catId, label: catNombre || 'Categoría' };
            setCategoriaSel(nextCat);
            await dispatch(cargarSubcategoriasFactura(catId));
        }

        if (!subcategoriaSel?.value && subId) {
            if (!nextCat?.value && catId) {
                await dispatch(cargarSubcategoriasFactura(catId));
            } else if (nextCat?.value) {
                await dispatch(cargarSubcategoriasFactura(nextCat.value));
            }
            nextSub = { value: subId, label: subNombre || 'Subcategoría' };
            setSubcategoriaSel(nextSub);
        }

        const catEnviar = nextCat?.value || null;
        const subEnviar = nextSub?.value || null;

        if (catEnviar || subEnviar) {
            await dispatch(
                cargarProductosFactura({
                    id_proveedor: proveedorSel.value,
                    id_categoria: catEnviar,
                    id_subcategoria: subEnviar,
                    nombre: null,
                    codigo: null,
                    tipo: 1,
                    codigo_proveedor: null
                })
            );
        }
    };

    const autocompletarDesdeProducto = (raw = {}) => {
        const inra =
            raw?.codigo_tienda ??
            raw?.codigo_inra ??
            raw?.codigoINRA ??
            raw?.codigo ??
            '';

        setCodigoInra(inra ? `${inra}` : '');

        const tc = getTc();
        const factor = Number(moneda) === 1 ? (1 / tc) : 1;

        const costoRaw =
            raw?.precio_unitario ??
            raw?.precio_historico ??
            raw?.precio ??
            null;

        const costoConv = costoRaw !== null && costoRaw !== undefined && costoRaw !== '' ? (toNum(costoRaw) * factor) : null;

        if (costoConv !== null) {
            setPrecioUnitario(round2(costoConv));
        } else {
            setPrecioUnitario('');
        }

        const costoNum = toNum(costoConv);

        const pubRaw = raw?.precio_publico;
        const minRaw = raw?.precio_minimo;
        const liqRaw = raw?.precio_liquidacion;

        const pub = pubRaw !== null && pubRaw !== undefined && pubRaw !== '' ? (toNum(pubRaw) * factor) : null;
        const min = minRaw !== null && minRaw !== undefined && minRaw !== '' ? (toNum(minRaw) * factor) : null;
        const liq = liqRaw !== null && liqRaw !== undefined && liqRaw !== '' ? (toNum(liqRaw) * factor) : null;

        if (pub !== null) {
            setPrecioPublico(round2(pub));
            if (costoNum > 0) setPctPublico(pctDesdePrecio(costoNum, pub));
        } else if (costoNum > 0) {
            setPrecioPublico(calcPrecioConPct(costoNum, pctPublico));
        } else {
            setPrecioPublico('');
        }

        if (min !== null) {
            setPrecioMinimo(round2(min));
            if (costoNum > 0) setPctMinimo(pctDesdePrecio(costoNum, min));
        } else if (costoNum > 0) {
            setPrecioMinimo(calcPrecioConPct(costoNum, pctMinimo));
        } else {
            setPrecioMinimo('');
        }

        if (liq !== null) {
            setPrecioLiquidacion(round2(liq));
            if (costoNum > 0) setPctLiquidacion(pctDesdePrecio(costoNum, liq));
        } else if (costoNum > 0) {
            setPrecioLiquidacion(calcPrecioConPct(costoNum, pctLiquidacion));
        } else {
            setPrecioLiquidacion('');
        }

        const tieneProveedor = raw?.codigo_proveedor !== null && raw?.codigo_proveedor !== undefined;
        if (tieneProveedor) {
            setCodigoProveedor(raw?.codigo_proveedor ? `${raw.codigo_proveedor}` : '');
        } else {
            setCodigoProveedor('');
        }
    };

    const onChangeProveedor = async (opt) => {
        setProveedorSel(opt || null);
        resetSeccionProductosCompleta();

        if (!opt?.value) return;

        await dispatch(cargarProductosFactura({
            id_proveedor: opt.value,
            id_categoria: null,
            id_subcategoria: null,
            nombre: null,
            codigo: null,
            tipo: 1,
            codigo_proveedor: null
        }));
    };

    const onChangeCategoria = async (opt) => {
        if (!proveedorSel?.value) {
            dispatch(setAlert('Primero selecciona un proveedor.', 'warning'));
            return;
        }

        setCategoriaSel(opt || null);
        setSubcategoriaSel(null);
        resetInputsProducto();

        if (!opt?.value) {
            await dispatch(cargarSubcategoriasFactura(null));
            await dispatch(cargarProductosFactura({
                id_proveedor: proveedorSel.value,
                id_categoria: null,
                id_subcategoria: null,
                nombre: null,
                codigo: null,
                tipo: 1,
                codigo_proveedor: null
            }));
            return;
        }

        await dispatch(cargarSubcategoriasFactura(opt.value));
        await dispatch(cargarProductosFactura({
            id_proveedor: proveedorSel.value,
            id_categoria: opt.value,
            id_subcategoria: null,
            nombre: null,
            codigo: null,
            tipo: 1,
            codigo_proveedor: null
        }));
    };

    const onChangeSubcategoria = async (opt) => {
        if (!proveedorSel?.value) {
            dispatch(setAlert('Primero selecciona un proveedor.', 'warning'));
            return;
        }

        setSubcategoriaSel(opt || null);
        resetInputsProducto();

        await dispatch(cargarProductosFactura({
            id_proveedor: proveedorSel.value,
            id_categoria: categoriaSel?.value || null,
            id_subcategoria: opt?.value || null,
            nombre: null,
            codigo: null,
            tipo: 1,
            codigo_proveedor: null
        }));
    };

    const onChangeProducto = async (opt) => {
        setProductoSel(opt || null);
        setUnidadSel('');
        setCantidad('1');
        setImpuestos('0');
        setCodigoInra('');
        setCodigoProveedor('');
        setPrecioUnitario('');
        setPrecioPublico('');
        setPrecioMinimo('');
        setPrecioLiquidacion('');
        setPctPublico('20');
        setPctMinimo('10');
        setPctLiquidacion('5');

        const raw = opt?.raw || {};
        autocompletarDesdeProducto(raw);

        if (!categoriaSel?.value || !subcategoriaSel?.value) {
            await syncCategoriaSubcategoriaDesdeProducto(raw);
        }
    };

    const onChangeCosto = (value) => {
        const clean = value || '';
        setPrecioUnitario(clean);

        const costo = toNum(clean);
        if (!costo || costo <= 0) {
            setPrecioPublico('');
            setPrecioMinimo('');
            setPrecioLiquidacion('');
            return;
        }

        setPrecioPublico(calcPrecioConPct(costo, pctPublico));
        setPrecioMinimo(calcPrecioConPct(costo, pctMinimo));
        setPrecioLiquidacion(calcPrecioConPct(costo, pctLiquidacion));
    };

    const onChangePctPublico = (v) => {
        const pct = v || '';
        setPctPublico(pct);
        const costo = toNum(precioUnitario);
        if (!costo || costo <= 0) {
            setPrecioPublico('');
            return;
        }
        setPrecioPublico(calcPrecioConPct(costo, pct));
    };

    const onChangePctMinimo = (v) => {
        const pct = v || '';
        setPctMinimo(pct);
        const costo = toNum(precioUnitario);
        if (!costo || costo <= 0) {
            setPrecioMinimo('');
            return;
        }
        setPrecioMinimo(calcPrecioConPct(costo, pct));
    };

    const onChangePctLiquidacion = (v) => {
        const pct = v || '';
        setPctLiquidacion(pct);
        const costo = toNum(precioUnitario);
        if (!costo || costo <= 0) {
            setPrecioLiquidacion('');
            return;
        }
        setPrecioLiquidacion(calcPrecioConPct(costo, pct));
    };

    const NoOptions = (props) => (
        <components.NoOptionsMessage {...props}>
            <div className="d-flex flex-column gap-2">
                <div>Sin resultados</div>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => {
                        if (!proveedorSel?.value) {
                            dispatch(setAlert('Selecciona un proveedor antes de crear un producto.', 'warning'));
                            return;
                        }
                        setModalNuevoProducto(true);
                    }}
                >
                    <i className="feather icon-plus" /> Agregar producto al catálogo
                </button>
            </div>
        </components.NoOptionsMessage>
    );

    const validarProductoCompleto = () => {
        if (!proveedorSel?.value) return 'Selecciona un proveedor';
        if (!productoSel?.value) return 'Selecciona un producto';

        if (!codigoInra?.trim()) return 'Falta el código INRA';
        if (!codigoProveedor?.trim()) return 'Falta el código del proveedor';

        if (!cantidad || Number(cantidad) <= 0) return 'Cantidad inválida';

        if (!precioUnitario || toNum(precioUnitario) <= 0) return 'Costo inválido';
        if (!precioPublico || toNum(precioPublico) <= 0) return 'Precio público inválido';
        if (!precioMinimo || toNum(precioMinimo) <= 0) return 'Precio mínimo inválido';
        if (!precioLiquidacion || toNum(precioLiquidacion) <= 0) return 'Precio liquidación inválido';

        if (!validarTipoCambioSiUSD()) return 'Tipo de cambio inválido';

        return '';
    };

    const onAgregarProductoFactura = async () => {
        const msg = validarProductoCompleto();
        if (msg) return dispatch(setAlert(msg, 'warning'));

        const tc = getTc();

        const costoBack = Number(moneda) === 1 ? (toNum(precioUnitario) * tc) : toNum(precioUnitario);
        const pubBack = Number(moneda) === 1 ? (toNum(precioPublico) * tc) : toNum(precioPublico);
        const minBack = Number(moneda) === 1 ? (toNum(precioMinimo) * tc) : toNum(precioMinimo);
        const liqBack = Number(moneda) === 1 ? (toNum(precioLiquidacion) * tc) : toNum(precioLiquidacion);

        const ok = await dispatch(
            crearProductoProveedorFactura({
                id_producto: productoSel.value,
                id_proveedor: proveedorSel.value,
                codigo_proveedor: codigoProveedor.trim(),
                precio_unitario: round2(costoBack),
                precio_publico: round2(pubBack),
                precio_minimo: round2(minBack),
                precio_liquidacion: round2(liqBack)
            })
        );

        if (!ok) return;

        const unidadObj = (opcionesUnidades || []).find(u => u.value === unidadSel) || null;

        const item = {
            _id: productoSel.value,
            nombre: productoSel.label,
            codigo_tienda: codigoInra.trim(),
            codigo_proveedor: codigoProveedor.trim(),
            cantidad_surtir: Number(cantidad),
            unidad: unidadSel ? unidadSel : null,
            unidad_nombre: unidadObj?.label || '',
            precio_unitario: toNum(precioUnitario),
            precio_publico: toNum(precioPublico),
            precio_minimo: toNum(precioMinimo),
            precio_liquidacion: toNum(precioLiquidacion),
            pct_publico: pctPublico,
            pct_minimo: pctMinimo,
            pct_liquidacion: pctLiquidacion,
            impuestos: Number(impuestos || 0)
        };

        setProductosFactura((prev) => [...prev, item]);
        dispatch(setAlert('Producto agregado a la factura', 'success'));
        resetInputsProducto();
    };

    const onEliminarProductoFactura = (idx) => {
        setProductosFactura((prev) => prev.filter((_, i) => i !== idx));
    };

    const onChangeCostoTabla = (idx, val) => {
        setProductosFactura((prev) => {
            const arr = Array.isArray(prev) ? [...prev] : [];
            const p = arr[idx];
            if (!p) return prev;

            const nuevoCosto = val === '' ? '' : Number(val);
            const costoNum = toNum(nuevoCosto);

            arr[idx] = {
                ...p,
                precio_unitario: nuevoCosto,
                precio_publico: calcPrecioConPctNum(costoNum, p.pct_publico),
                precio_minimo: calcPrecioConPctNum(costoNum, p.pct_minimo),
                precio_liquidacion: calcPrecioConPctNum(costoNum, p.pct_liquidacion)
            };
            return arr;
        });
    };

    const onChangeImpuestosTabla = (idx, val) => {
        setProductosFactura((prev) => {
            const arr = Array.isArray(prev) ? [...prev] : [];
            const p = arr[idx];
            if (!p) return prev;
            arr[idx] = { ...p, impuestos: val === '' ? '' : Number(val) };
            return arr;
        });
    };

    const onChangePctProductoTabla = (idx, tipo, val) => {
        setProductosFactura((prev) => {
            const arr = Array.isArray(prev) ? [...prev] : [];
            const p = arr[idx];
            if (!p) return prev;

            if (tipo === 'publico') {
                const nuevoPrecio = calcPrecioConPctNum(p?.precio_unitario, val);
                arr[idx] = { ...p, pct_publico: val, precio_publico: nuevoPrecio };
                return arr;
            }

            if (tipo === 'minimo') {
                const nuevoPrecio = calcPrecioConPctNum(p?.precio_unitario, val);
                arr[idx] = { ...p, pct_minimo: val, precio_minimo: nuevoPrecio };
                return arr;
            }

            const nuevoPrecio = calcPrecioConPctNum(p?.precio_unitario, val);
            arr[idx] = { ...p, pct_liquidacion: val, precio_liquidacion: nuevoPrecio };
            return arr;
        });
    };

    const puedeGuardar = Boolean(
        proveedorSel?.value &&
        numeroFactura?.trim() &&
        montoReal &&
        toNum(montoReal) > 0 &&
        productosFactura.length > 0
    );

    const onGuardarFactura = () => {
        if (!proveedorSel?.value) return dispatch(setAlert('Selecciona un proveedor', 'warning'));
        if (!numeroFactura?.trim()) return dispatch(setAlert('El número de factura es requerido', 'warning'));
        if (!montoReal || toNum(montoReal) <= 0) return dispatch(setAlert('Monto inválido', 'warning'));
        if (!productosFactura.length) return dispatch(setAlert('Agrega al menos un producto a la factura', 'warning'));
        if (!validarTipoCambioSiUSD()) return dispatch(setAlert('Tipo de cambio inválido', 'warning'));

        setShowConfirmarFactura(true);
    };

    const confirmarGuardarFactura = async () => {
        const payload = {
            id,
            id_proveedor: proveedorSel.value,
            id_metodo: null,
            numero: numeroFactura.trim(),
            monto_real: toNum(montoReal),
            fecha: formatYYYYMMDD(fecha || new Date()),
            productos: productosFactura.map(p => ({
                ...p,
                unidad: p?.unidad || null
            })),
            moneda: Number(moneda) === 1 ? 1 : 0,
            tipo_cambio: Number(moneda) === 1 ? getTc() : 1
        };

        setGuardandoFactura(true);
        const ok = await dispatch(editarFactura(payload));
        setGuardandoFactura(false);

        if (!ok) return;

        setShowConfirmarFactura(false);
        navigate('/facturas');
    };

    const puedeSeleccionarCatalogos = Boolean(proveedorSel?.value);
    const prefMoneda = Number(moneda) === 1 ? 'USD $' : '$';

    const subtotalProductos = useMemo(() => {
        return (productosFactura || []).reduce((acc, p) => {
            const qty = toNum(p?.cantidad_surtir);
            const unit = toNum(p?.precio_unitario);
            return acc + (qty * unit);
        }, 0);
    }, [productosFactura]);

    const totalProductos = useMemo(() => {
        return (productosFactura || []).reduce((acc, p) => {
            const qty = toNum(p?.cantidad_surtir);
            const unit = toNum(p?.precio_unitario);
            const imp = toNum(p?.impuestos);
            const unitConImp = unit * (1 + (imp / 100));
            return acc + (qty * unitConImp);
        }, 0);
    }, [productosFactura]);

    return (
        <Fragment>
            <div className="page-wrapper">
                <div className="page-body">
                    <div className="card mb-3">
                        <div className="card-header d-flex align-items-center justify-content-between">
                            <h5 className="mb-0">Editar factura</h5>
                            <Link to="/facturas" className="btn btn-outline-secondary">
                                <i className="feather icon-arrow-left" /> Volver
                            </Link>
                        </div>

                        <div className="card-body">
                            <div className="row">
                                <div className="col-12 col-md-2 mb-3">
                                    <div className="form-floating">
                                        <DatePicker
                                            selected={fecha}
                                            onChange={(d) => setFecha(d)}
                                            dateFormat="dd/MM/yyyy"
                                            customInput={<DateInput id="fecha_factura_editar" />}
                                        />
                                        <label htmlFor="fecha_factura_editar">Fecha</label>
                                    </div>
                                </div>

                                <div className="col-12 col-md-3 mb-3">
                                    <div className="input-group h-100">
                                        <Select
                                            options={opcionesProveedores}
                                            value={proveedorSel}
                                            onChange={onChangeProveedor}
                                            isClearable
                                            placeholder="Proveedor"
                                            noOptionsMessage={() => 'Sin resultados'}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            components={{ ValueContainer: CustomValueContainer }}
                                            styles={{ ...styles_select }}
                                            className="select-group w-100"
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                </div>

                                <div className="col-12 col-md-2 mb-3">
                                    <div className="form-floating">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Número de factura"
                                            id="numero_factura_editar"
                                            value={numeroFactura}
                                            onChange={(e) => setNumeroFactura(e.target.value)}
                                        />
                                        <label htmlFor="numero_factura_editar">Número de factura</label>
                                    </div>
                                </div>

                                <div className="col-12 col-md-2 mb-3">
                                    <div className="form-floating">
                                        <CurrencyInput
                                            className="form-control text-center"
                                            value={montoReal}
                                            decimalsLimit={3}
                                            groupSeparator=","
                                            decimalSeparator="."
                                            placeholder="Monto"
                                            id="monto_real_editar"
                                            onValueChange={(value) => setMontoReal(value || '')}
                                        />
                                        <label htmlFor="monto_real_editar">Monto</label>
                                    </div>
                                </div>

                                <div className="col-6 col-md-1 mb-3">
                                    <div className="form-floating">
                                        <select
                                            className="form-select"
                                            id="moneda_factura"
                                            value={moneda}
                                            onChange={(e) => onChangeMoneda(e.target.value)}
                                        >
                                            <option value={0}>MXN</option>
                                            <option value={1}>USD</option>
                                        </select>
                                        <label htmlFor="moneda_factura">Moneda</label>
                                    </div>
                                </div>

                                <div className="col-6 col-md-2 mb-3">
                                    <div className="form-floating">
                                        <input
                                            type="number"
                                            className="form-control text-center"
                                            id="tipo_cambio"
                                            placeholder="Tipo de cambio"
                                            value={Number(moneda) === 1 ? tipoCambioUsd : '1'}
                                            onChange={(e) => setTipoCambioUsd(e.target.value)}
                                            disabled={Number(moneda) !== 1}
                                            min="0"
                                            step="0.0001"
                                        />
                                        <label htmlFor="tipo_cambio">Tipo de cambio</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-header">
                            <h6 className="mb-0">Productos de la factura</h6>
                        </div>

                        <div className="card-body">
                            <div className="row">
                                <div className="col-12 col-md-3 mb-3">
                                    <div className="input-group h-100">
                                        <Select
                                            options={opcionesCategorias}
                                            value={categoriaSel}
                                            onChange={onChangeCategoria}
                                            isClearable
                                            placeholder={puedeSeleccionarCatalogos ? 'Categoría' : 'Selecciona proveedor primero'}
                                            isDisabled={!puedeSeleccionarCatalogos}
                                            noOptionsMessage={() => 'Sin resultados'}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            components={{ ValueContainer: CustomValueContainer }}
                                            styles={{ ...styles_select }}
                                            className="select-group w-100"
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                </div>

                                <div className="col-12 col-md-3 mb-3">
                                    <div className="input-group h-100">
                                        <Select
                                            options={opcionesSubcategorias}
                                            value={subcategoriaSel}
                                            onChange={onChangeSubcategoria}
                                            isClearable
                                            placeholder="Subcategoría"
                                            isDisabled={!puedeSeleccionarCatalogos || !categoriaSel}
                                            noOptionsMessage={() => 'Sin resultados'}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            components={{ ValueContainer: CustomValueContainer }}
                                            styles={{ ...styles_select }}
                                            className="select-group w-100"
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                </div>

                                <div className="col-12 col-md-6 mb-3">
                                    <div className="input-group h-100">
                                        <Select
                                            options={opcionesProductos}
                                            value={productoSel}
                                            onChange={onChangeProducto}
                                            isClearable
                                            isDisabled={!puedeSeleccionarCatalogos}
                                            placeholder="Producto"
                                            components={{ NoOptionsMessage: NoOptions, ValueContainer: CustomValueContainer }}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            styles={{ ...styles_select }}
                                            className="select-group w-100"
                                            classNamePrefix="react-select"
                                            filterOption={filterProductoOption}
                                        />
                                    </div>
                                </div>

                                <div className="col-12 col-md-3 mb-3">
                                    <div className="form-floating">
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={codigoInra}
                                            onChange={(e) => setCodigoInra(e.target.value)}
                                            disabled={!puedeSeleccionarCatalogos}
                                            placeholder="Código INRA"
                                            id="codigo_inra_editar"
                                        />
                                        <label htmlFor="codigo_inra_editar">Código INRA</label>
                                    </div>
                                </div>

                                <div className="col-12 col-md-3 mb-3">
                                    <div className="form-floating">
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={codigoProveedor}
                                            onChange={(e) => setCodigoProveedor(e.target.value)}
                                            placeholder="Código proveedor"
                                            id="codigo_proveedor_editar"
                                            disabled={!puedeSeleccionarCatalogos}
                                        />
                                        <label htmlFor="codigo_proveedor_editar">Código proveedor</label>
                                    </div>
                                </div>

                                <div className="col-12 col-md-3 mb-3">
                                    <div className="input-group">
                                        <div className="form-floating flex-grow-1">
                                            <input
                                                type="number"
                                                className="form-control text-center"
                                                min="1"
                                                value={cantidad}
                                                onChange={(e) => setCantidad(e.target.value)}
                                                placeholder="Cantidad"
                                                id="cantidad_editar"
                                                disabled={!productoSel}
                                            />
                                            <label htmlFor="cantidad_editar">Cantidad</label>
                                        </div>

                                        <div className="form-floating" style={{ width: 170 }}>
                                            <select
                                                className="form-select"
                                                id="unidad_producto"
                                                value={unidadSel}
                                                onChange={(e) => setUnidadSel(e.target.value)}
                                                disabled={!productoSel}
                                            >
                                                <option value="">Sin unidad</option>
                                                {opcionesUnidades.map((u) => (
                                                    <option key={u.value} value={u.value}>{u.label}</option>
                                                ))}
                                            </select>
                                            <label htmlFor="unidad_producto">Unidad</label>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12 col-md-3 mb-3">
                                    <div className="form-floating">
                                        <CurrencyInput
                                            className="form-control text-center"
                                            value={precioUnitario}
                                            decimalsLimit={3}
                                            groupSeparator=","
                                            decimalSeparator="."
                                            placeholder="Costo"
                                            id="costo_editar"
                                            onValueChange={(value) => onChangeCosto(value)}
                                            disabled={!productoSel}
                                        />
                                        <label htmlFor="costo_editar">Costo</label>
                                    </div>
                                </div>

                                <div className="col-12 col-md-3 mb-3">
                                    <div className="input-group">
                                        <div className="form-floating" style={{ width: 140 }}>
                                            <input
                                                type="number"
                                                className="form-control text-center"
                                                value={pctPublico}
                                                onChange={(e) => onChangePctPublico(e.target.value)}
                                                placeholder="% Público"
                                                id="pct_publico_editar"
                                                disabled={!productoSel}
                                            />
                                            <label htmlFor="pct_publico_editar">% Público</label>
                                        </div>

                                        <div className="form-floating flex-grow-1">
                                            <CurrencyInput
                                                className="form-control text-center"
                                                value={precioPublico}
                                                decimalsLimit={3}
                                                groupSeparator=","
                                                decimalSeparator="."
                                                placeholder="Precio público"
                                                id="precio_publico_editar"
                                                onValueChange={() => {}}
                                                disabled
                                            />
                                            <label htmlFor="precio_publico_editar">Precio público</label>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12 col-md-3 mb-3">
                                    <div className="input-group">
                                        <div className="form-floating" style={{ width: 140 }}>
                                            <input
                                                type="number"
                                                className="form-control text-center"
                                                value={pctMinimo}
                                                onChange={(e) => onChangePctMinimo(e.target.value)}
                                                placeholder="% Mínimo"
                                                id="pct_minimo_editar"
                                                disabled={!productoSel}
                                            />
                                            <label htmlFor="pct_minimo_editar">% Mínimo</label>
                                        </div>

                                        <div className="form-floating flex-grow-1">
                                            <CurrencyInput
                                                className="form-control text-center"
                                                value={precioMinimo}
                                                decimalsLimit={3}
                                                groupSeparator=","
                                                decimalSeparator="."
                                                placeholder="Precio mínimo"
                                                id="precio_minimo_editar"
                                                onValueChange={() => {}}
                                                disabled
                                            />
                                            <label htmlFor="precio_minimo_editar">Precio mínimo</label>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12 col-md-3 mb-3">
                                    <div className="input-group">
                                        <div className="form-floating" style={{ width: 140 }}>
                                            <input
                                                type="number"
                                                className="form-control text-center"
                                                value={pctLiquidacion}
                                                onChange={(e) => onChangePctLiquidacion(e.target.value)}
                                                placeholder="% Liquidación"
                                                id="pct_liquidacion_editar"
                                                disabled={!productoSel}
                                            />
                                            <label htmlFor="pct_liquidacion_editar">% Liquidación</label>
                                        </div>

                                        <div className="form-floating flex-grow-1">
                                            <CurrencyInput
                                                className="form-control text-center"
                                                value={precioLiquidacion}
                                                decimalsLimit={3}
                                                groupSeparator=","
                                                decimalSeparator="."
                                                placeholder="Precio liquidación"
                                                id="precio_liquidacion_editar"
                                                onValueChange={() => {}}
                                                disabled
                                            />
                                            <label htmlFor="precio_liquidacion_editar">Precio liquidación</label>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12 col-md-3 mb-3">
                                    <div className="form-floating">
                                        <input
                                            type="number"
                                            className="form-control text-center"
                                            min="0"
                                            value={impuestos}
                                            onChange={(e) => setImpuestos(e.target.value)}
                                            placeholder="Impuestos %"
                                            id="impuestos_editar"
                                            disabled={!productoSel}
                                        />
                                        <label htmlFor="impuestos_editar">Impuestos %</label>
                                    </div>
                                </div>

                                <div className="col-12 d-flex justify-content-end mb-3">
                                    <button
                                        type="button"
                                        className="btn btn-outline-success"
                                        onClick={onAgregarProductoFactura}
                                        disabled={!productoSel}
                                    >
                                        <i className="feather icon-plus-circle" /> Agregar producto a la factura
                                    </button>
                                </div>
                            </div>

                            <div className="table-responsive mt-3">
                                <table className="table table-striped table-hover">
                                    <thead>
                                        <tr>
                                            <th>Producto</th>
                                            <th>C.INRA</th>
                                            <th>C.Prov</th>
                                            <th className="text-end">Cantidad</th>
                                            <th className="text-end">Unidad</th>
                                            <th className="text-end">Costo</th>
                                            <th className="text-end">Público</th>
                                            <th className="text-end">Mínimo</th>
                                            <th className="text-end">Liquidación</th>
                                            <th className="text-end">Impuestos</th>
                                            <th className="text-end">Opciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!productosFactura.length && (
                                            <tr>
                                                <td colSpan="11" className="text-center">
                                                    Sin productos agregados
                                                </td>
                                            </tr>
                                        )}

                                        {productosFactura.map((p, idx) => (
                                            <tr key={`${p._id}-${idx}`}>
                                                <td>{p.nombre}</td>
                                                <td>{p.codigo_tienda || '—'}</td>
                                                <td>{p.codigo_proveedor || '—'}</td>
                                                <td className="text-end">{p.cantidad_surtir}</td>
                                                <td className="text-end">{p.unidad_nombre || '—'}</td>
                                                <td className="text-end">
                                                    <div className="d-flex flex-column align-items-end">
                                                        <div className="input-group input-group-sm mt-1" style={{ width: 120 }}>
                                                            <input
                                                                type="number"
                                                                className="form-control text-center"
                                                                style={{ width: 100 }}
                                                                step="any"
                                                                value={p.precio_unitario}
                                                                onChange={(e) => onChangeCostoTabla(idx, e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                </td>

                                                <td className="text-end">
                                                    <div className="d-flex flex-column align-items-end">
                                                        <div>{`${prefMoneda} ${moneyFmt(p.precio_publico)}`}</div>
                                                        <div className="input-group input-group-sm mt-1" style={{ width: 120 }}>
                                                            <input
                                                                type="number"
                                                                className="form-control text-center"
                                                                value={p.pct_publico ?? ''}
                                                                onChange={(e) => onChangePctProductoTabla(idx, 'publico', e.target.value)}
                                                            />
                                                            <span className="input-group-text">%</span>
                                                        </div>
                                                    </div>
                                                </td>

                                                <td className="text-end">
                                                    <div className="d-flex flex-column align-items-end">
                                                        <div>{`${prefMoneda} ${moneyFmt(p.precio_minimo)}`}</div>
                                                        <div className="input-group input-group-sm mt-1" style={{ width: 120 }}>
                                                            <input
                                                                type="number"
                                                                className="form-control text-center"
                                                                value={p.pct_minimo ?? ''}
                                                                onChange={(e) => onChangePctProductoTabla(idx, 'minimo', e.target.value)}
                                                            />
                                                            <span className="input-group-text">%</span>
                                                        </div>
                                                    </div>
                                                </td>

                                                <td className="text-end">
                                                    <div className="d-flex flex-column align-items-end">
                                                        <div>{`${prefMoneda} ${moneyFmt(p.precio_liquidacion)}`}</div>
                                                        <div className="input-group input-group-sm mt-1" style={{ width: 120 }}>
                                                            <input
                                                                type="number"
                                                                className="form-control text-center"
                                                                value={p.pct_liquidacion ?? ''}
                                                                onChange={(e) => onChangePctProductoTabla(idx, 'liquidacion', e.target.value)}
                                                            />
                                                            <span className="input-group-text">%</span>
                                                        </div>
                                                    </div>
                                                </td>

                                                <td className="text-end">
                                                    <div className="d-flex flex-column align-items-end">
                                                        <div className="input-group input-group-sm mt-1" style={{ width: 120 }}>
                                                            <input
                                                                type="number"
                                                                className="form-control text-center"
                                                                style={{ width: 90 }}
                                                                step="any"
                                                                value={p.impuestos ?? 0}
                                                                onChange={(e) => onChangeImpuestosTabla(idx, e.target.value)}
                                                            />
                                                            <span className="input-group-text">%</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="text-end">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => onEliminarProductoFactura(idx)}
                                                    >
                                                        <i className="fas fa-trash text-danger" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="d-flex justify-content-end mt-3">
                                <div className="text-end">
                                    <div className="fw-semibold">Subtotal productos: {prefMoneda} {moneyFmt(subtotalProductos)}</div>
                                    <div className="fw-semibold">Total productos (c/ impuestos): {prefMoneda} {moneyFmt(totalProductos)}</div>
                                </div>
                            </div>

                            <div className="d-flex justify-content-end mt-3">
                                <button
                                    type="button"
                                    className="btn btn-success"
                                    onClick={onGuardarFactura}
                                    disabled={!puedeGuardar || guardandoFactura}
                                >
                                    <i className="fa fa-save" /> Guardar cambios
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <NuevoProductoModal
                show={modalNuevoProducto}
                onHide={() => setModalNuevoProducto(false)}
                categoriaDefault={categoriaSel}
                onProductoCreado={async (producto) => {
                    const opt = {
                        value: producto._id,
                        label: producto.nombre || 'Producto',
                        raw: producto
                    };

                    setProductoSel(opt);
                    setUnidadSel('');
                    setCantidad('1');
                    setImpuestos('0');
                    setCodigoInra('');
                    setCodigoProveedor('');
                    setPrecioUnitario('');
                    setPrecioPublico('');
                    setPrecioMinimo('');
                    setPrecioLiquidacion('');
                    setPctPublico('20');
                    setPctMinimo('10');
                    setPctLiquidacion('5');

                    autocompletarDesdeProducto(producto);

                    if (!categoriaSel?.value || !subcategoriaSel?.value) {
                        await syncCategoriaSubcategoriaDesdeProducto(producto);
                    }

                    if (proveedorSel?.value) {
                        await dispatch(
                            cargarProductosFactura({
                                id_proveedor: proveedorSel.value,
                                id_categoria: categoriaSel?.value || null,
                                id_subcategoria: subcategoriaSel?.value || null,
                                nombre: null,
                                codigo: null,
                                tipo: 1,
                                codigo_proveedor: null
                            })
                        );
                    }
                }}
            />

            <ConfirmarGuardarFacturaModal
                show={showConfirmarFactura}
                onHide={() => setShowConfirmarFactura(false)}
                onConfirm={confirmarGuardarFactura}
                monto={toNum(montoReal)}
                totalCalculado={totalProductos}
                prefMoneda={prefMoneda}
                loading={guardandoFactura}
                titulo="Confirmar cambios en factura"
            />
        </Fragment>
    );
};

export default EditarFactura;